import { useMemo, useState, type CSSProperties } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { Arrow } from "@/components/icons";
import { routeHead } from "@/components/seo";
import { breadcrumbSchema } from "@/lib/schema";
import { site } from "@/content";
import { articles } from "@/content/insights";
import { Aurora, Grain, Particles, TiltCard, spotlightPointer } from "@/components/motion";

const ROUTE = "/insights";
export const Route = createFileRoute("/insights/")({
  head: () =>
    routeHead(ROUTE, {
      title: "Bookkeeping Insights for Australian Businesses | NextEra",
      description:
        "A working library on bookkeeping, BAS, payroll, Xero, AI automation and business finance for Australian owners. Plain answers, no jargon.",
      jsonLd: breadcrumbSchema([
        { name: "Home", item: "/" },
        { name: "Insights", item: ROUTE },
      ]),
    }),
  component: Page,
});

function formatDate(iso: string) {
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function Page() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<string | null>(null);

  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    for (const a of articles) counts.set(a.category, (counts.get(a.category) ?? 0) + 1);
    return [...counts.entries()].sort((x, y) => y[1] - x[1]);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return articles.filter((a) => {
      if (category && a.category !== category) return false;
      if (!q) return true;
      const hay = `${a.title} ${a.description} ${a.category} ${a.keywords.join(" ")}`.toLowerCase();
      return q.split(/\s+/).every((word) => hay.includes(word));
    });
  }, [query, category]);

  const showFeatured = !query && !category;
  const [featured, ...rest] = articles;
  const list = showFeatured ? rest : filtered;

  return (
    <div className="bg-background text-foreground">
      <section className="horizon-field--light border-b border-line">
        <div className="relative z-10 mx-auto max-w-6xl px-6 py-16 md:py-20">
          <h1 className="hero-enter mt-4 max-w-3xl font-serif text-4xl leading-[1.1] text-ink sm:text-5xl">
            The working library for owners who want their numbers{" "}
            <span className="text-shimmer" style={{ "--shimmer-base": "var(--ink)" } as CSSProperties}>
              working
            </span>
            .
          </h1>
          <p className="hero-enter-delay mt-6 max-w-2xl text-lg leading-relaxed text-ink/80">
            {articles.length} plain-language guides on bookkeeping, BAS, payroll, Xero, cash flow
            and AI in the back office. Written by the NextEra team, checked against how the rules
            actually work.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <label className="relative block w-full max-w-md">
              <Search
                className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--subtle)]"
                aria-hidden
              />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search the library…"
                aria-label="Search articles"
                className="w-full rounded-full border border-[color:var(--mist)] bg-white/80 py-3 pl-11 pr-4 text-sm text-[color:var(--navy)] backdrop-blur placeholder:text-[color:var(--subtle)] focus:border-[color:var(--brand)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]/25"
              />
            </label>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setCategory(null)}
              className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${
                category === null
                  ? "border-[color:var(--navy)] bg-[color:var(--navy)] text-white"
                  : "border-[color:var(--mist)] bg-white/70 text-[color:var(--ink)] hover:border-[color:var(--brand)]"
              }`}
            >
              All
            </button>
            {categories.map(([c, n]) => (
              <button
                key={c}
                type="button"
                onClick={() => setCategory(category === c ? null : c)}
                className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${
                  category === c
                    ? "border-[color:var(--navy)] bg-[color:var(--navy)] text-white"
                    : "border-[color:var(--mist)] bg-white/70 text-[color:var(--ink)] hover:border-[color:var(--brand)]"
                }`}
              >
                {c} <span className="opacity-60">{n}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {showFeatured && featured && (
        <section className="border-b border-line bg-background">
          <div className="reveal mx-auto max-w-6xl px-6 py-14">
            <TiltCard>
              <Link
                to="/insights/$slug"
                params={{ slug: featured.slug }}
                className="card-premium group grid gap-8 !bg-stone/70 p-8 md:grid-cols-[1.4fr_1fr] md:p-10"
              >
                <div>
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-teal">
                    Featured · {featured.category}
                  </span>
                  <h2 className="mt-4 font-serif text-2xl leading-snug text-ink sm:text-3xl">
                    {featured.title}
                  </h2>
                  <p className="mt-4 max-w-xl leading-relaxed text-ink/80">
                    {featured.description}
                  </p>
                  <span className="link-underline mt-6 inline-flex items-center gap-1 text-sm font-medium text-teal-text">
                    Read the guide
                    <Arrow className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </span>
                </div>
                <div className="hidden items-end justify-end text-sm text-subtle md:flex">
                  {formatDate(featured.date)} · {featured.readingMinutes} min read
                </div>
              </Link>
            </TiltCard>
          </div>
        </section>
      )}

      <section className="border-b border-line">
        <div className="mx-auto max-w-6xl px-6 py-14">
          {list.length === 0 ? (
            <p className="py-10 text-center text-subtle">
              Nothing matches that search yet. Try “BAS”, “payroll” or “cash flow”, or{" "}
              <Link to="/contact" className="link-underline font-medium text-teal-text">
                ask us directly
              </Link>
              .
            </p>
          ) : (
            <div className="stagger grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {list.map((a) => (
                <TiltCard key={a.slug} className="h-full">
                  <Link
                    to="/insights/$slug"
                    params={{ slug: a.slug }}
                    className="card-premium group flex h-full flex-col p-7"
                  >
                    <span className="text-xs font-semibold uppercase tracking-[0.2em] text-teal">
                      {a.category}
                    </span>
                    <h2 className="mt-3 font-serif text-xl leading-snug text-ink">{a.title}</h2>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-ink/80">
                      {a.description}
                    </p>
                    <span className="mt-5 text-xs text-subtle">{a.readingMinutes} min read</span>
                  </Link>
                </TiltCard>
              ))}
            </div>
          )}
        </div>
      </section>

      <section onMouseMove={spotlightPointer} className="spotlight horizon-field text-white">
        <Aurora />
        <Grain />
        <Particles count={10} />
        <div className="relative z-10 mx-auto max-w-4xl px-6 py-16 text-center">
          <h2 className="reveal text-shimmer font-serif text-2xl sm:text-3xl">
            Reading about it is the slow way to fix it.
          </h2>
          <p className="reveal mx-auto mt-3 max-w-2xl text-white/75">
            One Strategic Finance Review with Jamee covers your setup, your numbers and your next
            steps.
          </p>
          <div className="reveal mt-6 flex flex-wrap justify-center gap-3">
            <Link to={site.primaryCta.href} className="btn-on-dark">
              {site.primaryCta.label}
              <Arrow className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
